import { useCallback, useEffect, useState, useRef } from 'react';
import { Loader2, Play, Volume2, VolumeX, Maximize, Pause } from 'lucide-react';
// Mock Player component until we have the proper package
// import { Player } from '@livepeer/react';

// Enhanced Player component with actual video playback
const Player = (props: any) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(props.muted || false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    if (!props.playbackId) return;
    
    // Use a demo video for testing
    const demoVideos = [
      'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/SubaruOutbackOnStreetAndDirt.mp4',
      'https://storage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4',
    ];
    
    // Use the playbackId to select a video (just for demo)
    const videoIndex = parseInt(props.playbackId.replace(/\D/g, '')) % demoVideos.length;
    const videoUrl = demoVideos[videoIndex || 0];
    
    if (videoRef.current) {
      videoRef.current.src = videoUrl;
      videoRef.current.muted = isMuted;
      
      if (props.autoPlay) {
        videoRef.current.play().catch(err => console.error('Autoplay failed:', err));
      }
    }
  }, [props.playbackId, props.autoPlay]);
  
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    
    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };
    
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      setProgress((video.currentTime / video.duration) * 100);
    };
    
    const handlePlay = () => {
      setIsPlaying(true);
      if (props.onPlay) props.onPlay();
    };
    
    const handlePause = () => {
      setIsPlaying(false);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
    };
    
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    
    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [props.onPlay]);
  
  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(err => console.error('Play failed:', err));
    }
  };
  
  const toggleMute = () => {
    if (!videoRef.current) return;
    
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };
  
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    
    videoRef.current.currentTime = pos * duration;
  };
  
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  const handleMouseMove = () => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };
  
  const toggleFullscreen = () => {
    if (!videoRef.current) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      videoRef.current.requestFullscreen();
    }
  };
  
  return (
    <div 
      className="relative aspect-video bg-dark-900 rounded-lg overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
        </div>
      )}
      
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        poster={props.poster}
        onClick={togglePlay}
      />
      
      {/* Video Controls */}
      <div 
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 transition-opacity duration-300 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Progress bar */}
        <div 
          className="w-full h-1 bg-gray-700 rounded-full mb-3 cursor-pointer"
          onClick={handleProgressClick}
        >
          <div 
            className="h-full bg-primary-500 rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              className="w-8 h-8 flex items-center justify-center rounded-full bg-dark-800/50 hover:bg-dark-700/70 transition-colors"
              onClick={togglePlay}
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            
            <button 
              className="w-8 h-8 flex items-center justify-center rounded-full bg-dark-800/50 hover:bg-dark-700/70 transition-colors"
              onClick={toggleMute}
            >
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            
            <span className="text-xs text-gray-300">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>
          
          <div>
            <button 
              className="w-8 h-8 flex items-center justify-center rounded-full bg-dark-800/50 hover:bg-dark-700/70 transition-colors"
              onClick={toggleFullscreen}
            >
              <Maximize className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
import { web3Service } from '@/services/web3';
import { useAuthStore } from '@/store/authStore';
import { FiLock, FiCreditCard, FiKey, FiShield } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount } from 'wagmi';

interface LivepeerPlayerProps {
  playbackId: string;
  title?: string;
  poster?: string;
  autoPlay?: boolean;
  muted?: boolean;
  isTokenGated?: boolean;
  contentId?: string;
  gateType?: 'token' | 'payment' | 'nft';
  onPlay?: () => void;
  price?: string;
  tokenAmount?: string;
  // nftId?: string;
}

const LivepeerPlayer = ({
  playbackId,
  title,
  poster,
  autoPlay = false,
  muted = false,
  isTokenGated = false,
  contentId,
  gateType = 'token',
  onPlay,
  price,
  tokenAmount,
  // nftId, // Used for NFT-gated content
}: LivepeerPlayerProps) => {
  const [hasAccess, setHasAccess] = useState(!isTokenGated);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated } = useAuthStore();
  const { isConnected } = useAccount();

  // Get playback info to check if the stream is active
  // const { data: playbackInfo } = usePlaybackInfo({ playbackId });
  // Using Player component directly instead of checking playback info

  // Check if user has access to token-gated content
  const checkAccess = useCallback(async () => {
    if (!isTokenGated || !contentId || !user?.walletAddress) {
      return;
    }

    try {
      setIsLoading(true);
      const access = await web3Service.hasAccess(user.walletAddress, contentId);
      setHasAccess(access);
    } catch (error) {
      console.error('Error checking access:', error);
      toast.error('Failed to check content access');
    } finally {
      setIsLoading(false);
    }
  }, [isTokenGated, contentId, user?.walletAddress]);

  // Check access when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      checkAccess();
    }
  }, [isAuthenticated, checkAccess]);

  // Handle unlocking content
  const handleUnlock = async () => {
    if (!isAuthenticated) {
      toast.error('Please connect your wallet to unlock this content');
      return;
    }

    if (!contentId) {
      toast.error('Content ID is missing');
      return;
    }

    try {
      setIsLoading(true);

      if (gateType === 'payment') {
        // Make payment to unlock
        await web3Service.makePayment(contentId);
      } else if (gateType === 'token') {
        // Verify token balance
        // await web3Service.verifyTokenBalance(contentId);
      } else if (gateType === 'nft') {
        // Verify NFT ownership
        // await web3Service.verifyNFTOwnership(contentId, nftId || '');
      }

      // Check access again
      await checkAccess();
      
      if (hasAccess) {
        toast.success('Content unlocked successfully!');
      }
    } catch (error) {
      console.error('Error unlocking content:', error);
      toast.error('Failed to unlock content');
    } finally {
      setIsLoading(false);
    }
  };

  // Get the appropriate icon based on gate type
  const getGateIcon = () => {
    switch (gateType) {
      case 'payment':
        return <FiCreditCard className="text-5xl mb-4 text-accent-500" />;
      case 'nft':
        return <FiKey className="text-5xl mb-4 text-secondary-500" />;
      default:
        return <FiLock className="text-5xl mb-4 text-primary-500" />;
    }
  };

  // Get the appropriate message based on gate type
  const getGateMessage = () => {
    switch (gateType) {
      case 'payment':
        return `Purchase this content for ${price || '5'} dUSD to unlock`;
      case 'nft':
        return 'You need the required NFT to unlock this content';
      default:
        return `You need ${tokenAmount || '100'} FTTF tokens to unlock this content`;
    }
  };

  // Render token-gated overlay if user doesn't have access
  if (isTokenGated && !hasAccess) {
    return (
      <div className="relative w-full aspect-video bg-dark-800 rounded-2xl overflow-hidden shadow-soft">
        {/* Blurred thumbnail */}
        {poster && (
          <div
            className="absolute inset-0 bg-cover bg-center blur-md opacity-30"
            style={{ backgroundImage: `url(${poster})` }}
          />
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/70 to-transparent"></div>

        {/* Lock overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass-card p-8 max-w-md w-full"
          >
            {getGateIcon()}
            <h3 className="text-2xl font-bold mb-2 text-gradient">{title || 'Token-Gated Content'}</h3>
            <p className="text-gray-300 mb-6">
              {getGateMessage()}
            </p>
            
            {!isConnected ? (
              <div className="flex flex-col items-center">
                <p className="text-gray-400 mb-4">Connect your wallet to access this content</p>
                <ConnectButton label="Connect Wallet" />
              </div>
            ) : !isAuthenticated ? (
              <div className="flex flex-col items-center">
                <p className="text-gray-400 mb-4">Please sign in to continue</p>
                <button
                  className="btn-primary"
                  onClick={() => toast.error('Authentication required')}
                >
                  Sign In
                </button>
              </div>
            ) : (
              <button
                className={`btn-${gateType === 'payment' ? 'accent' : gateType === 'nft' ? 'secondary' : 'primary'} btn-lg w-full`}
                onClick={handleUnlock}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                    <span>Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {gateType === 'payment' ? <FiCreditCard className="mr-2" /> : <FiShield className="mr-2" />}
                    <span>{gateType === 'payment' ? 'Purchase Access' : 'Unlock Content'}</span>
                  </div>
                )}
              </button>
            )}
            
            <div className="mt-4 text-xs text-gray-500">
              By unlocking, you agree to the platform's terms of service
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Render the player if user has access
  return (
    <div className="w-full rounded-2xl overflow-hidden shadow-soft">
      <Player
        playbackId={playbackId}
        title={title}
        poster={poster}
        autoPlay={autoPlay}
        muted={muted}
        theme={{
          borderStyles: {
            containerBorderStyle: undefined,
          },
          colors: {
            accent: '#5a96ff',
          },
          space: {
            controlsBottomMarginX: '1rem',
            controlsBottomMarginY: '0.75rem',
          },
          radii: {
            containerBorderRadius: '1rem',
          },
        }}
        onPlay={onPlay}
      />
    </div>
  );
};

export default LivepeerPlayer;